import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { buildGoogleAdsRedirectUri } from "@/lib/googleAdsOAuth";

const GoogleAdsCallback = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"working" | "exchanging" | "success" | "error">("working");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");
      const oauthError = params.get("error");

      if (oauthError) {
        setStatus("error");
        setError(`Google denied the request: ${oauthError}`);
        toast.error(`Google Ads connection failed: ${oauthError}`);
        return;
      }
      if (!code || !state) {
        setStatus("error");
        setError("Missing code or state in callback URL.");
        toast.error("Google Ads connection failed: missing code/state.");
        return;
      }

      const { data: auth } = await supabase.auth.getUser();
      const uid = auth.user?.id;
      if (!uid) {
        setStatus("error");
        setError("Not logged in. Please log in and reconnect Google Ads.");
        toast.error("Google Ads connection failed: not logged in.");
        return;
      }
      if (uid !== state) {
        setStatus("error");
        setError("Session mismatch — refusing to attach Google Ads to a different account.");
        toast.error("Google Ads connection failed: session mismatch.");
        return;
      }

      setStatus("exchanging");
      const { data, error: invokeErr } = await supabase.functions.invoke(
        "exchange-google-ads-code",
        { body: { code, state, redirectUri: buildGoogleAdsRedirectUri() } },
      );
      const payload: any = data;
      if (invokeErr || payload?.ok === false) {
        const msg = payload?.error || invokeErr?.message || "Token exchange failed.";
        setStatus("error");
        setError(msg);
        toast.error(`Google Ads connection failed: ${msg}`);
        return;
      }

      setStatus("success");
      toast.success("Google Ads connected successfully");
      setTimeout(() => navigate("/dashboard?tab=integrations", { replace: true }), 900);
    })();
  }, [navigate]);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background text-foreground">
      <div className="max-w-md text-center px-4">
        {status === "working" && (
          <div className="text-sm font-mono text-muted-foreground">Waiting for Google approval…</div>
        )}
        {status === "exchanging" && (
          <div className="text-sm font-mono text-muted-foreground">Storing Google Ads tokens…</div>
        )}
        {status === "success" && (
          <div className="text-sm font-mono text-primary">✅ Google Ads connected. Redirecting…</div>
        )}
        {status === "error" && (
          <>
            <div className="text-sm font-mono text-red-500 mb-3">
              ❌ {error || "Could not connect Google Ads."}
            </div>
            <button
              onClick={() => navigate("/dashboard?tab=integrations")}
              className="text-xs font-mono underline text-muted-foreground"
            >
              Back to Integrations
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleAdsCallback;

